import React, { useRef, useState } from "react";
import { toast } from "sonner";
import {
  Send, Loader2, FileText, AlertCircle, ListChecks, ClipboardList,
  CalendarClock, FileUp, X, Printer, CheckCircle2, BadgeAlert,
} from "lucide-react";
import { useI18n } from "../lib/i18n";
import { api } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";

const ACCEPTED_TYPES = ".pdf,.docx,.doc,.txt,.md,.png,.jpg,.jpeg,.webp,.heic,.bmp,.tiff";
const MAX_FILES = 8;

const URGENCY_STYLE = {
  high: "bg-red-50 text-red-800 border-red-200",
  medium: "bg-amber-50 text-amber-800 border-amber-200",
  low: "bg-emerald-50 text-emerald-800 border-emerald-200",
};

const urgencyLabel = (u, isAr) => {
  if (u === "high") return isAr ? "عاجل" : "High";
  if (u === "medium") return isAr ? "متوسط" : "Medium";
  if (u === "low") return isAr ? "غير عاجل" : "Low";
  return u || "—";
};

export default function CaseAssessment() {
  const { lang } = useI18n();
  const isAr = lang === "ar";

  const [description, setDescription] = useState("");
  const [files, setFiles] = useState([]);
  const [contactName, setContactName] = useState("");
  const [contactEmail, setContactEmail] = useState("");
  const [contactPhone, setContactPhone] = useState("");
  const [busy, setBusy] = useState(false);
  const [assessment, setAssessment] = useState(null);
  const [assessmentId, setAssessmentId] = useState(null);
  const fileRef = useRef(null);

  // Appointment state
  const [apptOpen, setApptOpen] = useState(false);
  const [apptName, setApptName] = useState("");
  const [apptPhone, setApptPhone] = useState("");
  const [apptEmail, setApptEmail] = useState("");
  const [apptDate, setApptDate] = useState("");
  const [apptTime, setApptTime] = useState("");
  const [apptNotes, setApptNotes] = useState("");
  const [apptBusy, setApptBusy] = useState(false);
  const [apptDone, setApptDone] = useState(false);

  const t = {
    eyebrow: isAr ? "تقييم قضيتك بالذكاء الاصطناعي" : "AI Case Assessment",
    title: isAr
      ? "اشرح قضيتك — يردّك سامي بخطّة قانونية ميسّرة."
      : "Describe your case — Sami returns a clear legal roadmap.",
    lead: isAr
      ? "صِف نزاعك أو مشكلتك القانونية وأرفق المستندات المتاحة. يُعطيك سامي ملخّصاً للنزاع، النقاط القانونية المهمة، المستندات الناقصة، الخطوات اللازمة، ومجال التخصّص المناسب من فريقنا. مجاني وفوري."
      : "Describe your dispute and attach any documents you have. Sami returns a dispute summary, key legal points, missing documents, required next steps, and the right practice area at our firm. Free and instant.",
    descLabel: isAr ? "اشرح قضيتك بالتفصيل" : "Describe your case in detail",
    descPh: isAr
      ? "ابدأ من البداية: ماذا حدث، ومتى، ومن الأطراف، وما الذي تبحث عنه؟"
      : "Start from the beginning: what happened, when, who is involved, and what outcome you want.",
    files: isAr ? "المستندات (اختياري)" : "Documents (optional)",
    filesLead: isAr
      ? "ارفع العقود، الإنذارات، الرسائل، الفواتير، أو الأحكام — يصل عدد الملفات إلى 8."
      : "Upload contracts, notices, correspondence, invoices, or judgments — up to 8 files.",
    dropZone: isAr
      ? "اضغط هنا أو اسحب الملفات (PDF · DOCX · صورة · نص)"
      : "Click here or drop files (PDF · DOCX · image · text)",
    fileLimit: isAr ? "حتى 25 م.ب. لكل ملف" : "Up to 25 MB per file",
    contact: isAr ? "بياناتك (اختياري — لإرسال نسخة)" : "Your details (optional — to email a copy)",
    name: isAr ? "الاسم" : "Name",
    email: isAr ? "البريد الإلكتروني" : "Email",
    phone: isAr ? "الهاتف" : "Phone",
    submit: isAr ? "احصل على التقييم" : "Get my assessment",
    analysing: isAr ? "جاري التحليل..." : "Analysing…",
    busyHint: isAr
      ? "يستغرق التقييم عادةً 60 إلى 120 ثانية."
      : "Assessment usually takes 60 to 120 seconds.",
    short: isAr ? "يُرجى تقديم وصف أكثر تفصيلاً (20 حرفاً على الأقل)." : "Please provide more detail (min 20 chars).",
    failed: isAr ? "فشل التحليل. حاول مرة أخرى." : "Assessment failed. Please try again.",
    success: isAr ? "تمّ التقييم" : "Assessment ready",
    emailed: isAr ? "وأُرسلت نسخة إلى بريدك." : "A copy was sent to your email.",
    summary: isAr ? "ملخّص النزاع" : "Dispute Summary",
    points: isAr ? "النقاط القانونية المهمة" : "Key Legal Points",
    missing: isAr ? "المستندات الناقصة" : "Missing Documents",
    steps: isAr ? "الخطوات اللازمة" : "Next Steps",
    area: isAr ? "مجال الممارسة المقترح" : "Recommended Practice Area",
    urgency: isAr ? "درجة الاستعجال" : "Urgency",
    print: isAr ? "حفظ / طباعة PDF" : "Save / Print PDF",
    newCase: isAr ? "قضية جديدة" : "New case",
    bookAppt: isAr ? "احجز موعداً مع المجموعة" : "Book an appointment with the firm",
    apptTitle: isAr ? "طلب موعد مع مجموعة الدخيل" : "Request an appointment with Aldakheel",
    apptLead: isAr
      ? "اترك بياناتك وتاريخك المفضّل، وسيتواصل معك أحد المحامين خلال يوم عمل."
      : "Leave your details and preferred date — a lawyer will reach out within one business day.",
    apptDate: isAr ? "التاريخ المفضّل" : "Preferred date",
    apptTime: isAr ? "الوقت المفضّل" : "Preferred time",
    apptNotes: isAr ? "ملاحظات (اختياري)" : "Notes (optional)",
    apptSubmit: isAr ? "إرسال الطلب" : "Send request",
    apptSending: isAr ? "جاري الإرسال..." : "Sending…",
    apptOk: isAr
      ? "تمّ استلام طلب الموعد. سيتواصل معك أحد المحامين خلال يوم عمل."
      : "Appointment request received. A lawyer will be in touch within one business day.",
    apptRequired: isAr ? "الاسم ورقم الهاتف مطلوبان." : "Name and phone are required.",
    disclaimer: isAr
      ? "هذا تقييم أوّلي مولّد بالذكاء الاصطناعي ولا يُغني عن استشارة محامٍ مرخّص."
      : "AI-generated preliminary assessment. Not a substitute for licensed counsel.",
  };

  const addFiles = (newOnes) => {
    const arr = Array.from(newOnes || []);
    const merged = [...files];
    for (const f of arr) {
      if (merged.length >= MAX_FILES) break;
      if (f.size > 25 * 1024 * 1024) {
        toast.error(`${f.name}: ${isAr ? "أكبر من 25 م.ب." : "max 25 MB"}`);
        continue;
      }
      merged.push(f);
    }
    setFiles(merged);
  };

  const onDrop = (e) => {
    e.preventDefault();
    addFiles(e.dataTransfer.files);
  };

  const submit = async () => {
    if (!description || description.trim().length < 20)
      return toast.error(t.short);
    setBusy(true);
    setAssessment(null);
    try {
      const fd = new FormData();
      fd.append("description", description);
      fd.append("language", lang);
      if (contactName) fd.append("name", contactName);
      if (contactEmail) fd.append("email", contactEmail);
      if (contactPhone) fd.append("phone", contactPhone);
      for (const f of files) fd.append("files", f);
      const r = await api.post("/case-assessment", fd, {
        headers: { "Content-Type": "multipart/form-data" },
        timeout: 240_000,
      });
      setAssessment(r.data.assessment);
      setAssessmentId(r.data.id);
      // Pre-fill the appointment form with whatever we already know.
      setApptName(contactName);
      setApptPhone(contactPhone);
      setApptEmail(contactEmail);
      toast.success(t.success + (r.data.email_sent ? " — " + t.emailed : ""));
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (err) {
      const msg = err?.response?.data?.detail || t.failed;
      toast.error(typeof msg === "string" ? msg : t.failed);
    } finally {
      setBusy(false);
    }
  };

  const submitAppt = async () => {
    if (!apptName || !apptPhone) return toast.error(t.apptRequired);
    setApptBusy(true);
    try {
      await api.post(`/case-assessment/${assessmentId}/appointment`, {
        name: apptName,
        phone: apptPhone,
        email: apptEmail || null,
        preferred_date: apptDate || null,
        preferred_time: apptTime || null,
        notes: apptNotes || null,
        language: lang,
      });
      setApptDone(true);
      toast.success(t.apptOk);
    } catch (err) {
      const msg = err?.response?.data?.detail || t.failed;
      toast.error(typeof msg === "string" ? msg : t.failed);
    } finally {
      setApptBusy(false);
    }
  };

  const printReport = () => {
    if (!assessment) return;
    const iframe = document.createElement("iframe");
    iframe.style.position = "fixed";
    iframe.style.right = "-10000px";
    document.body.appendChild(iframe);
    const doc = iframe.contentDocument;
    const blocks = [];
    if (assessment.dispute_summary)
      blocks.push(`<h2>${t.summary}</h2><p>${assessment.dispute_summary}</p>`);
    if (assessment.legal_points?.length) {
      blocks.push(
        `<h2>${t.points}</h2><ul>` +
          assessment.legal_points
            .map(
              (p) =>
                `<li><strong>${p.title || ""}</strong><br>${p.detail || ""}</li>`,
            )
            .join("") +
          "</ul>",
      );
    }
    if (assessment.missing_documents?.length) {
      blocks.push(
        `<h2>${t.missing}</h2><ul>` +
          assessment.missing_documents.map((m) => `<li>${m}</li>`).join("") +
          "</ul>",
      );
    }
    if (assessment.next_steps?.length) {
      blocks.push(
        `<h2>${t.steps}</h2><ol>` +
          assessment.next_steps
            .map(
              (n) =>
                `<li><strong>${n.action || ""}</strong><br>${n.why || ""}</li>`,
            )
            .join("") +
          "</ol>",
      );
    }
    doc.open();
    doc.write(`<!DOCTYPE html><html lang="${lang}" dir="${isAr ? "rtl" : "ltr"}">
<head><meta charset="utf-8"><title>${t.eyebrow}</title>
<style>
@page { size: A4; margin: 18mm; }
body { font-family: 'Times New Roman', Times, serif; color:#1c1917; line-height:1.7; }
header { border-bottom: 2px solid #1c1917; padding-bottom: 12px; margin-bottom: 18px; }
header h1 { margin:0; font-family:Georgia,serif; font-size: 22px; }
header p { margin:4px 0 0; color:#78716c; font-size:12px; letter-spacing:0.12em; text-transform:uppercase; }
h2 { font-size: 16px; margin-top: 22px; border-bottom: 1px solid #cbd5e1; padding-bottom: 5px; font-family: Georgia, serif; }
ul, ol { padding-${isAr ? "right" : "left"}: 20px; }
li { margin-bottom: 8px; }
.meta { background:#f8fafc; border:1px solid #e2e8f0; padding:10px 14px; }
footer { margin-top: 32px; border-top: 1px solid #cbd5e1; padding-top: 10px; color:#78716c; font-size:12px; }
</style></head>
<body>
<header>
  <h1>${isAr ? "مجموعة الدخيل القانونية" : "Aldakheel Legal Group"}</h1>
  <p>${t.eyebrow}</p>
</header>
<div class="meta">
  <strong>${t.area}:</strong> ${assessment.recommended_practice_area || "—"}<br>
  <strong>${t.urgency}:</strong> ${urgencyLabel(assessment.urgency, isAr)}
</div>
${blocks.join("")}
<footer>${assessment.disclaimer || t.disclaimer}</footer>
</body></html>`);
    doc.close();
    iframe.contentWindow.focus();
    setTimeout(() => {
      iframe.contentWindow.print();
      setTimeout(() => document.body.removeChild(iframe), 1500);
    }, 250);
  };

  const reset = () => {
    setAssessment(null);
    setAssessmentId(null);
    setDescription("");
    setFiles([]);
    setApptOpen(false);
    setApptDone(false);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  return (
    <div data-testid="case-assessment-page" className="bg-white">
      <section className="border-b border-slate-200">
        <div className="max-w-5xl mx-auto px-6 lg:px-10 pt-16 pb-12">
          <p className="eyebrow">{t.eyebrow}</p>
          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl mt-4 leading-[1.05] tracking-tight">
            {t.title}
          </h1>
          <p className="mt-6 max-w-3xl text-lg text-slate-600 leading-relaxed">{t.lead}</p>
        </div>
      </section>

      <section className="max-w-5xl mx-auto px-6 lg:px-10 py-12">
        {!assessment && (
          <div className="bg-slate-50 border border-slate-200 p-6 lg:p-10">
            <div>
              <Label htmlFor="case-desc" className="text-sm font-medium text-slate-900">
                {t.descLabel}
              </Label>
              <Textarea
                id="case-desc"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder={t.descPh}
                rows={9}
                data-testid="case-description-input"
                className="rounded-sm bg-white mt-2"
              />
            </div>

            <div className="mt-8">
              <h3 className="text-sm font-medium text-slate-900">{t.files}</h3>
              <p className="text-xs text-slate-500 mt-1">{t.filesLead}</p>
              <div
                onDragOver={(e) => e.preventDefault()}
                onDrop={onDrop}
                onClick={() => fileRef.current?.click()}
                data-testid="case-dropzone"
                className="mt-3 border-2 border-dashed border-slate-300 hover:border-slate-900 transition-colors p-8 text-center cursor-pointer bg-white"
              >
                <FileUp className="w-8 h-8 mx-auto text-slate-400 mb-2" />
                <p className="text-sm text-slate-700">{t.dropZone}</p>
                <p className="text-xs text-slate-500 mt-1">{t.fileLimit}</p>
                <input
                  ref={fileRef}
                  type="file"
                  multiple
                  accept={ACCEPTED_TYPES}
                  className="hidden"
                  data-testid="case-file-input"
                  onChange={(e) => addFiles(e.target.files)}
                />
              </div>
              {files.length > 0 && (
                <div className="mt-3 grid sm:grid-cols-2 gap-2">
                  {files.map((f, i) => (
                    <div
                      key={`${f.name}-${f.size}-${i}`}
                      className="flex items-center justify-between bg-white border border-slate-200 px-3 py-2 text-sm"
                      data-testid={`case-file-chip-${i}`}
                    >
                      <div className="flex items-center gap-2 truncate">
                        <FileText className="w-4 h-4 text-slate-500 shrink-0" />
                        <span className="truncate">{f.name}</span>
                        <span className="text-slate-400 text-xs shrink-0">
                          ({(f.size / 1024).toFixed(0)} KB)
                        </span>
                      </div>
                      <button
                        type="button"
                        onClick={() =>
                          setFiles(files.filter((_, j) => j !== i))
                        }
                        data-testid={`case-file-remove-${i}`}
                        className="text-slate-400 hover:text-slate-900 shrink-0"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="mt-8 grid md:grid-cols-3 gap-3">
              <div className="md:col-span-3">
                <Label className="text-xs uppercase tracking-wider text-slate-500">
                  {t.contact}
                </Label>
              </div>
              <Input
                value={contactName}
                onChange={(e) => setContactName(e.target.value)}
                placeholder={t.name}
                data-testid="case-contact-name"
                className="rounded-sm bg-white"
              />
              <Input
                value={contactEmail}
                onChange={(e) => setContactEmail(e.target.value)}
                placeholder={t.email}
                type="email"
                data-testid="case-contact-email"
                className="rounded-sm bg-white"
              />
              <Input
                value={contactPhone}
                onChange={(e) => setContactPhone(e.target.value)}
                placeholder={t.phone}
                data-testid="case-contact-phone"
                className="rounded-sm bg-white"
              />
            </div>

            <Button
              onClick={submit}
              disabled={busy}
              data-testid="case-submit-btn"
              className="mt-8 rounded-sm h-12 px-8 bg-slate-900 hover:bg-slate-800 text-white"
            >
              {busy ? (
                <>
                  <Loader2 className="w-4 h-4 me-2 animate-spin" />
                  {t.analysing}
                </>
              ) : (
                <>
                  <Send className="w-4 h-4 me-2" />
                  {t.submit}
                </>
              )}
            </Button>
            {busy && (
              <p className="mt-3 text-sm text-slate-500" data-testid="case-busy-hint">
                {t.busyHint}
              </p>
            )}
          </div>
        )}

        {assessment && (
          <div data-testid="case-result">
            <div className="flex items-end justify-between flex-wrap gap-4 mb-8">
              <div>
                <p className="eyebrow flex items-center gap-2">
                  <ClipboardList className="w-4 h-4" />
                  {t.eyebrow}
                </p>
                <h2 className="font-display text-3xl mt-2">
                  {assessment.recommended_practice_area || (isAr ? "تقييم القضية" : "Case Assessment")}
                </h2>
                <div className="mt-3 flex flex-wrap items-center gap-3 text-sm">
                  <span className="text-slate-500">{t.urgency}:</span>
                  <span
                    className={`inline-block px-3 py-1 border ${URGENCY_STYLE[assessment.urgency] || "bg-slate-50 border-slate-200 text-slate-700"}`}
                    data-testid="case-urgency-badge"
                  >
                    {urgencyLabel(assessment.urgency, isAr)}
                  </span>
                </div>
              </div>
              <div className="flex gap-2">
                <Button
                  onClick={printReport}
                  variant="outline"
                  className="rounded-sm"
                  data-testid="case-print-btn"
                >
                  <Printer className="w-4 h-4 me-2" />
                  {t.print}
                </Button>
                <Button
                  onClick={reset}
                  variant="outline"
                  className="rounded-sm"
                  data-testid="case-reset-btn"
                >
                  {t.newCase}
                </Button>
              </div>
            </div>

            {assessment.dispute_summary && (
              <div className="bg-slate-50 border border-slate-200 p-6 mb-6">
                <h3 className="eyebrow flex items-center gap-2">
                  <FileText className="w-4 h-4" />
                  {t.summary}
                </h3>
                <p className="mt-3 text-slate-700 leading-relaxed whitespace-pre-wrap" data-testid="case-summary">
                  {assessment.dispute_summary}
                </p>
              </div>
            )}

            {assessment.legal_points?.length > 0 && (
              <div className="mb-6" data-testid="case-points">
                <h3 className="eyebrow flex items-center gap-2 mb-4">
                  <BadgeAlert className="w-4 h-4 text-indigo-600" />
                  {t.points}
                </h3>
                <div className="grid gap-3">
                  {assessment.legal_points.map((p, i) => (
                    <div
                      key={`point-${i}-${(p.title || "").slice(0, 24)}`}
                      className="border border-slate-200 bg-white p-4"
                      data-testid={`case-point-${i}`}
                    >
                      <h4 className="font-medium text-slate-900">{p.title}</h4>
                      <p className="text-sm text-slate-700 mt-2 leading-relaxed whitespace-pre-wrap">
                        {p.detail}
                      </p>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {assessment.missing_documents?.length > 0 && (
              <div className="mb-6" data-testid="case-missing">
                <h3 className="eyebrow flex items-center gap-2 mb-4">
                  <AlertCircle className="w-4 h-4 text-amber-600" />
                  {t.missing}
                </h3>
                <ul className="bg-amber-50 border border-amber-200 p-5 space-y-2">
                  {assessment.missing_documents.map((m, i) => (
                    <li
                      key={`missing-${i}-${(m || "").slice(0, 24)}`}
                      className="flex items-start gap-2 text-amber-900 text-sm"
                      data-testid={`case-missing-${i}`}
                    >
                      <span className="w-1.5 h-1.5 rounded-full bg-amber-600 mt-2 shrink-0" />
                      <span>{m}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {assessment.next_steps?.length > 0 && (
              <div className="mb-8" data-testid="case-steps">
                <h3 className="eyebrow flex items-center gap-2 mb-4">
                  <ListChecks className="w-4 h-4 text-emerald-600" />
                  {t.steps}
                </h3>
                <ol className="grid gap-3">
                  {assessment.next_steps.map((n, i) => (
                    <li
                      key={`step-${i}-${(n.action || "").slice(0, 24)}`}
                      className="border border-slate-200 bg-white p-4 grid grid-cols-[36px_1fr] gap-4"
                      data-testid={`case-step-${i}`}
                    >
                      <div className="font-display text-2xl text-emerald-700">
                        {n.order || i + 1}
                      </div>
                      <div>
                        <h4 className="font-medium text-slate-900">{n.action}</h4>
                        <p className="text-sm text-slate-600 mt-1 leading-relaxed">{n.why}</p>
                      </div>
                    </li>
                  ))}
                </ol>
              </div>
            )}

            {/* Appointment CTA */}
            <div className="border-t border-slate-200 pt-8 mt-8">
              {!apptOpen && !apptDone && (
                <div className="bg-slate-950 text-white p-8 lg:p-10">
                  <p className="eyebrow text-slate-400">
                    {isAr ? "الخطوة التالية" : "Next step"}
                  </p>
                  <h3 className="font-display text-3xl lg:text-4xl mt-3 leading-tight">
                    {isAr
                      ? "تحدّث مع محامٍ من مجموعة الدخيل."
                      : "Talk to a lawyer at Aldakheel."}
                  </h3>
                  <p className="mt-4 text-slate-300 max-w-2xl">
                    {isAr
                      ? "اطلب موعداً مجانياً مع محامي المجموعة المختص. سنتواصل معك خلال يوم عمل."
                      : "Request a free intake call with a specialist lawyer. We respond within one business day."}
                  </p>
                  <Button
                    onClick={() => setApptOpen(true)}
                    data-testid="case-open-appt-btn"
                    className="mt-6 rounded-sm bg-white text-slate-900 hover:bg-slate-100"
                  >
                    <CalendarClock className="w-4 h-4 me-2" />
                    {t.bookAppt}
                  </Button>
                </div>
              )}

              {apptOpen && !apptDone && (
                <div
                  className="bg-white border border-slate-200 p-8"
                  data-testid="case-appt-form"
                >
                  <p className="eyebrow">{t.apptTitle}</p>
                  <p className="mt-2 text-slate-600">{t.apptLead}</p>
                  <div className="mt-6 grid md:grid-cols-2 gap-4">
                    <div>
                      <Label className="text-xs uppercase tracking-wider text-slate-500">{t.name} *</Label>
                      <Input
                        value={apptName}
                        onChange={(e) => setApptName(e.target.value)}
                        data-testid="appt-name-input"
                        className="rounded-sm mt-2"
                      />
                    </div>
                    <div>
                      <Label className="text-xs uppercase tracking-wider text-slate-500">{t.phone} *</Label>
                      <Input
                        value={apptPhone}
                        onChange={(e) => setApptPhone(e.target.value)}
                        data-testid="appt-phone-input"
                        className="rounded-sm mt-2"
                      />
                    </div>
                    <div>
                      <Label className="text-xs uppercase tracking-wider text-slate-500">{t.email}</Label>
                      <Input
                        value={apptEmail}
                        onChange={(e) => setApptEmail(e.target.value)}
                        type="email"
                        data-testid="appt-email-input"
                        className="rounded-sm mt-2"
                      />
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <Label className="text-xs uppercase tracking-wider text-slate-500">{t.apptDate}</Label>
                        <Input
                          type="date"
                          value={apptDate}
                          onChange={(e) => setApptDate(e.target.value)}
                          data-testid="appt-date-input"
                          className="rounded-sm mt-2"
                        />
                      </div>
                      <div>
                        <Label className="text-xs uppercase tracking-wider text-slate-500">{t.apptTime}</Label>
                        <Input
                          type="time"
                          value={apptTime}
                          onChange={(e) => setApptTime(e.target.value)}
                          data-testid="appt-time-input"
                          className="rounded-sm mt-2"
                        />
                      </div>
                    </div>
                  </div>
                  <div className="mt-4">
                    <Label className="text-xs uppercase tracking-wider text-slate-500">{t.apptNotes}</Label>
                    <Textarea
                      value={apptNotes}
                      onChange={(e) => setApptNotes(e.target.value)}
                      rows={3}
                      data-testid="appt-notes-input"
                      className="rounded-sm mt-2"
                    />
                  </div>
                  <div className="mt-6 flex gap-2">
                    <Button
                      onClick={submitAppt}
                      disabled={apptBusy}
                      data-testid="appt-submit-btn"
                      className="rounded-sm bg-slate-900 hover:bg-slate-800 text-white"
                    >
                      {apptBusy ? (
                        <>
                          <Loader2 className="w-4 h-4 me-2 animate-spin" />
                          {t.apptSending}
                        </>
                      ) : (
                        <>
                          <Send className="w-4 h-4 me-2" />
                          {t.apptSubmit}
                        </>
                      )}
                    </Button>
                    <Button
                      onClick={() => setApptOpen(false)}
                      variant="outline"
                      className="rounded-sm"
                      data-testid="appt-cancel-btn"
                    >
                      {isAr ? "إلغاء" : "Cancel"}
                    </Button>
                  </div>
                </div>
              )}

              {apptDone && (
                <div
                  className="bg-emerald-50 border border-emerald-200 p-8 text-center"
                  data-testid="case-appt-success"
                >
                  <CheckCircle2 className="w-10 h-10 mx-auto text-emerald-700" />
                  <h3 className="font-display text-2xl mt-3 text-emerald-900">
                    {t.success}
                  </h3>
                  <p className="mt-2 text-emerald-800">{t.apptOk}</p>
                </div>
              )}
            </div>

            <p className="text-xs text-slate-500 mt-8 leading-relaxed" data-testid="case-disclaimer">
              {assessment.disclaimer || t.disclaimer}
              {assessmentId ? ` · ID: ${assessmentId.slice(0, 8)}` : ""}
            </p>
          </div>
        )}
      </section>
    </div>
  );
}
